import "./cleaner.css";
import { useEffect } from "react";
import type { ScheduleOccurrence } from "../../shared/contracts";
import { addDays, localDate } from "../../shared/time";
import type { Punch, Visit } from "../../shared/types";
import { ErrorBanner, Loading } from "../components/ui";
import { fmtDate, fmtDuration, fmtTime } from "../lib/format";
import { onFlushed } from "../lib/offlineQueue";
import { useApi } from "../lib/useApi";
import { usePunchQueue } from "./hooks";
import { plainFlags } from "./wording";

const HISTORY_DAYS = 14;
const SCHEDULE_DAYS = 7;

/** Friendly badges for a punch: only shown when it's not a plain "OK". */
function PunchBadges({ punch, label }: { punch: Punch; label: string }) {
  const result = {
    ok: null,
    flagged: null, // the flags below say why
    pending: ["info", `${label} waiting for approval`],
    approved: ["ok", `${label} approved`],
    rejected: ["danger", `${label} not approved`],
  }[punch.result];
  return (
    <>
      {result && <span className={`badge ${result[0]}`}>{result[1]}</span>}
      {plainFlags(punch.flags).map((f) => (
        <span key={`${label}-${f}`} className="badge warn">
          {f}
        </span>
      ))}
    </>
  );
}

function VisitCard({ v }: { v: Visit }) {
  const statusBadge = {
    open: ["info", "On site now"],
    closed: null,
    missing_clock_out: ["danger", "No clock-out — the office will sort it"],
    rejected: ["danger", "Not approved"],
  }[v.status];
  return (
    <li className="cl-visit card flat">
      <div className="row between cl-nowrap">
        <span className="cl-visit-date">{fmtDate(v.in.at)}</span>
        <span className="cl-visit-total mono">{v.minutes != null ? fmtDuration(v.minutes) : "—"}</span>
      </div>
      <div className="cl-visit-site">{v.siteName}</div>
      <div className="muted mono">
        {fmtTime(v.in.at)} – {v.out ? fmtTime(v.out.at) : v.status === "open" ? "now" : "?"}
        {v.breakMinutes > 0 && <span> · {fmtDuration(v.breakMinutes)} break</span>}
      </div>
      <div className="cl-badges">
        {statusBadge && <span className={`badge ${statusBadge[0]}`}>{statusBadge[1]}</span>}
        <PunchBadges punch={v.in} label="Clock-in" />
        {v.out && <PunchBadges punch={v.out} label="Clock-out" />}
      </div>
    </li>
  );
}

function Schedule({ today }: { today: string }) {
  const { data, error, loading } = useApi<ScheduleOccurrence[]>(
    `/me/schedule?from=${today}&to=${addDays(today, SCHEDULE_DAYS - 1)}`,
  );
  // Scheduling is optional: if it isn't available, just leave the section out.
  if (error) return null;
  if (loading && !data) return null;
  const upcoming = (data ?? []).filter((o) => o.status !== "done" && o.status !== "missed");
  return (
    <section className="stack">
      <h2>Coming up</h2>
      {upcoming.length === 0 ? (
        <p className="muted">Nothing booked for the next 7 days.</p>
      ) : (
        <ul className="list card flat cl-schedule">
          {upcoming.map((o) => (
            <li key={`${o.scheduleId}-${o.date}`} className="row between cl-nowrap">
              <span>
                <strong>{o.date === today ? "Today" : fmtIsoDate(o.date)}</strong>
                <span className="cl-block">{o.siteName}</span>
              </span>
              <span className="mono cl-right">
                {o.startTime}–{o.endTime}
                {o.status === "late" && <span className="badge warn cl-block">Late</span>}
                {o.status === "clocked_in" && <span className="badge info cl-block">Clocked in</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** "Thu 5 Nov" for a YYYY-MM-DD date (noon UTC is the same date in the UK). */
function fmtIsoDate(date: string): string {
  return fmtDate(Date.parse(`${date}T12:00:00Z`));
}

export function HistoryPage() {
  const today = localDate(Date.now());
  const from = addDays(today, -(HISTORY_DAYS - 1));
  const { data, error, loading, reload } = useApi<Visit[]>(`/me/visits?from=${from}&to=${today}`);
  usePunchQueue(); // keeps the offline sender running on this screen too
  // Saved punches sent from this screen change the list.
  useEffect(() => onFlushed(() => void reload()), [reload]);

  // Rejected visits aren't paid, so they don't count towards the total.
  const total = (data ?? []).reduce((sum, v) => sum + (v.status === "rejected" ? 0 : (v.minutes ?? 0)), 0);

  return (
    <div className="page-narrow cl-page">
      <h1>History</h1>
      <Schedule today={today} />
      <section className="stack">
        <div className="row between">
          <h2 className="cl-tight">Last 14 days</h2>
          {data && data.length > 0 && (
            <span className="muted">
              Total <strong className="mono">{fmtDuration(total)}</strong>
            </span>
          )}
        </div>
        <ErrorBanner error={error} onRetry={reload} />
        {loading && !data ? (
          <Loading />
        ) : data && data.length === 0 ? (
          <p className="muted">No visits in the last 14 days.</p>
        ) : (
          <ul className="cl-visits">
            {(data ?? []).map((v) => (
              <VisitCard key={v.id} v={v} />
            ))}
          </ul>
        )}
        <p className="small muted">Hours shown are before any rounding the office applies.</p>
      </section>
    </div>
  );
}
